import React, { useState, useEffect } from 'react';
import { LoadingOverlay } from '@mantine/core';
import { useAuth } from '../../contexts/AuthContext';
import { getUserRegistrations, deleteRegistration } from '../../services/registrationService';
import RegistrationCard from './RegistrationCard';
import { notifications } from '@mantine/notifications';
import { isSeasonPastForUser } from '../../config/seasons';

/**
 * CompetitionSection Component
 * Displays competition registration section in profile
 */
const CompetitionSection = () => {
  const { user } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  /**
   * Load user's registrations
   */
  useEffect(() => {
    const loadRegistrations = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('📋 Loading registrations for user:', user.id);
        const data = await getUserRegistrations(user.id);
        console.log('✅ Registrations loaded:', data);
        console.log('Number of registrations:', data?.length);
        if (data && data.length > 0) {
          console.log('Most recent registration:', data[0]);
          console.log('Status:', data[0].status);
          console.log('Current step:', data[0].current_step);
        }
        setRegistrations(data || []);
      } catch (error) {
        console.error('❌ Error loading registrations:', error);
        console.error('Error details:', error.message, error.code, error.details);

        // Set empty array so UI still renders
        setRegistrations([]);

        // Only show notification if it's not a "table doesn't exist" error
        if (!error.message?.includes('relation') && !error.message?.includes('does not exist')) {
          notifications.show({
            title: 'Note',
            message: 'Registration data will be available after setup',
            color: 'blue'
          });
        }
      } finally {
        setLoading(false);
      }
    };

    loadRegistrations();
  }, [user]);

  /**
   * Handle delete registration
   */
  const handleDelete = async (registrationId) => {
    if (!window.confirm('Are you sure you want to delete this draft registration?')) {
      return;
    }

    try {
      await deleteRegistration(registrationId);
      setRegistrations(prev => prev.filter(r => r.id !== registrationId));
      notifications.show({
        title: 'Success',
        message: 'Draft registration deleted',
        color: 'green'
      });
    } catch (error) {
      console.error('Error deleting registration:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to delete registration',
        color: 'red'
      });
    }
  };

  // Get the CURRENT-season individual registration, preferring paid/complete
  // over drafts. Registrations from seasons whose competition day has passed
  // live in PastCompetitionsSection instead.
  const individualRegs = registrations.filter(
    r => r.registration_type === 'individual' && !isSeasonPastForUser(r.competition_year, user?.email)
  );
  const currentRegistration = individualRegs.find(r =>
    r.payment_status === 'paid' || r.status === 'complete' || r.status === 'paid' || r.status === 'submitted'
  ) || individualRegs[0] || null;

  return (
    <div style={{ position: 'relative' }}>
      {loading ? (
        <div style={{ position: 'relative', minHeight: '300px' }}>
          <LoadingOverlay visible={true} />
        </div>
      ) : (
        <RegistrationCard
          registration={currentRegistration}
          onDelete={() => handleDelete(currentRegistration?.id)}
        />
      )}
    </div>
  );
};

export default CompetitionSection;
